/**
 * PWA-UPDATE-1 — recovering from a lazy chunk that no longer exists.
 *
 * THE FAILURE, verified live against app.aisistoo.com rather than assumed:
 *   GET /assets/index-CDMbOgSp.js   (a real entry chunk from a superseded deploy)
 *     -> HTTP 200, Content-Type: text/html, 3357 bytes — the CURRENT index.html.
 *   GET /assets/index-ZZZZZZZZ.js   (a hash that never existed)
 *     -> identical response.
 * Cloudflare Pages keeps roughly the previous deploy's assets and then stops; after that the SPA
 * catch-all in public/_redirects ("/* -> /index.html 200") answers. A browser importing that as an ES
 * module does not get a 404 it can reason about — it gets a MIME/parse failure, React.lazy memoises
 * the rejection so a retry cannot help, and the ErrorBoundary renders a full-screen crash card.
 *
 * This app is heavily code-split: seven lazy shells and roughly forty lazy screens. So the window
 * between "a new worker claimed and evicted the old precache" and "the page reloads" is a window in
 * which any navigation can hit this. That window is exactly what swUpdate.ts opens when it defers a
 * reload, which is why this net ships in the same commit and not later.
 *
 * WHY NOT FIX IT AT THE EDGE: because it cannot be. Cloudflare Pages does not support 404 as a
 * _redirects status (the docs list 301/302/303/307/308 and give "/blog/* /blog/404.html 404" as an
 * explicitly unsupported example), and the docs also state redirects are followed regardless of
 * whether an asset matches — which contradicts the observed behaviour above. Adding an /assets/* rule
 * to fix a white screen could therefore shadow every real asset and take the whole app down. The
 * immutable /assets/* caching in public/_headers is the safe half of the mitigation; this is the other.
 *
 * WHY NOT preventDefault() ON THE EVENT: it makes Vite's preload helper resolve the import to
 * `undefined`, and React then reads `undefined.default`. That converts a recoverable error into a
 * different crash. We listen and reload; we do not suppress.
 */

const CHUNK_BREAKER_KEY = 'aisistoo_chunk_reload_at';
const CHUNK_BREAKER_MS = 60_000;

/**
 * Shared by the window listener and ErrorBoundary so there is one definition of "this is a dead
 * chunk, not a bug in our code" rather than two that drift apart.
 */
export function isChunkLoadError(message: string): boolean {
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload|dynamically imported module/i.test(
    message
  );
}

/**
 * @returns true if a reload was started; false if the breaker refused, in which case the CALLER must
 * show real failure UI. A dead chunk that survives a reload is not a deploy artefact any more — it is
 * a genuine repeating failure, and hiding it behind an endless reload loop would be far worse than a
 * crash card.
 */
export function attemptChunkReload(): boolean {
  let last = 0;
  try {
    const raw = window.sessionStorage.getItem(CHUNK_BREAKER_KEY);
    last = raw ? Number(raw) || 0 : 0;
  } catch {
    /* blocked storage — fall through and allow exactly this one reload */
  }
  if (last > 0 && Date.now() - last < CHUNK_BREAKER_MS) return false;

  try {
    window.sessionStorage.setItem(CHUNK_BREAKER_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

export function initChunkReloadNet(): void {
  window.addEventListener('vite:preloadError', () => {
    attemptChunkReload();
  });
}
